import {
  MessageEnvelope,
  getQueryTypeStarttoken,
  buildGroupedMessages,
  getVisibleMessages,
  mergeAndSortMessages,
  TIMETOKEN_MULTIPLIER,
  getGrowGroupRanges,
  MessageListItem,
} from '@zonofi/common';
import {
  getChannelMessages,
  useAppSelector,
  getCurrentChannelPagination,
  updateMessagesByChannelId,
  dispatch,
  updateChannelPagination,
  getIsScrollToBottom,
  updateScrollToBottom,
  getWorkspaceId,
  useLazyGetInternalNotesQuery,
  getCurrentChannelInternalNotePaginationKey,
  updateInternalNotesPagination,
  InternalNoteLastKey,
  InternalNotesApiResponse,
  useLazyGetMessageHistoryQuery,
  useLazyGetConversationThreadsQuery,
  getSignals,
  getCurrentSignal,
} from '@zonofi/seller-rtk-store';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isUndefined } from 'lodash';
import { usePrevious } from '../hooks/previous';
import { convertInternalNoteToMessage } from '../functions/message-list-helpers';

interface MessageListCoreProps {
  channelId: string;
  customerId?: string;
  isZoey?: boolean;
  includeInternalNotes?: boolean;
  isSignalView?: boolean;
}

interface FetchHistoryOptions {
  resetExisting?: boolean;
  endTimeToken?: string;
}

export const useMessageListCore = ({
  channelId,
  customerId,
  isZoey = false,
  includeInternalNotes = false,
  isSignalView = false,
}: MessageListCoreProps) => {
  // Apis
  const [getInternalNotes] = useLazyGetInternalNotesQuery();
  const [fetchMsgHistory] = useLazyGetMessageHistoryQuery();
  const [getConversationThreads] = useLazyGetConversationThreadsQuery();

  // Selectors
  const goToBottom = useAppSelector(getIsScrollToBottom);
  const messages = useAppSelector(
    getChannelMessages(channelId)
  ) as MessageEnvelope[];
  const workspaceId = useAppSelector(getWorkspaceId);
  const paginationEnd = useAppSelector(getCurrentChannelPagination(channelId));
  const internalNotePaginationKey = useAppSelector(
    getCurrentChannelInternalNotePaginationKey(channelId)
  );
  const {
    conversationId: currentSignal,
    startTime: signalStartTime,
    endTime: signalEndTime,
  } = useAppSelector(getCurrentSignal);
  const prevSignal = usePrevious(currentSignal);

  const signals = useAppSelector(getSignals);
  const signalData = isSignalView ? Object.values(signals) : [];

  const internalNotesApplicable =
    includeInternalNotes && !!customerId && !isSignalView;

  // Previous values
  const prevChannelId = usePrevious(channelId);
  const prevMessages = usePrevious(messages);

  const hasUsedGrowTokenRef = useRef<boolean>(false);

  const fetchingRef = useRef(false);

  // Local state
  const [scrolledBottom, setScrolledBottom] = useState(true);
  const [scrolledTop, setScrolledTop] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [initMessagesLoaded, setInitMessagesLoaded] = useState<
    Record<string, boolean>
  >({});
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [internalNotesPaginationEnd, setInternalNotesPaginationEnd] =
    useState(false);

  // Track whether we need to continue fetching internal notes
  const [shouldFetchMoreNotes, setShouldFetchMoreNotes] = useState(false);

  // Memoized dispatchers
  const setMessages = useCallback(
    (messages: MessageEnvelope[]) => {
      dispatch(updateMessagesByChannelId({ channelId, messages }));
    },
    [channelId]
  );

  const setPaginationEnd = useCallback(
    (value: boolean) => {
      dispatch(updateChannelPagination({ channelId, pagination: value }));
    },
    [channelId]
  );

  const setInternalNotePaginationKey = useCallback(
    (value: InternalNoteLastKey) => {
      dispatch(updateInternalNotesPagination({ channelId, pagination: value }));
    },
    [channelId]
  );

  const activeMessages = useMemo(() => {
    return getVisibleMessages(messages, paginationEnd);
  }, [messages, paginationEnd]);

  const growGroupRanges = useMemo(() => {
    return getGrowGroupRanges(messages, signalData);
  }, [signalData, messages]);

  const groupedMessages: MessageListItem[] = useMemo(() => {
    return buildGroupedMessages(activeMessages, growGroupRanges);
  }, [activeMessages, growGroupRanges]);

  // Scroll handlers
  const toBottom = useCallback(() => {
    setUnreadMessages(0);
    setScrolledBottom(true);
    dispatch(updateScrollToBottom(false));
  }, []);

  const toTop = useCallback(() => {
    setUnreadMessages(0);
    setScrolledTop(true);
  }, []);

  // Fetch internal notes with pagination awareness
  const fetchInternalNotes = useCallback(async (): Promise<
    InternalNotesApiResponse | undefined
  > => {
    if (!internalNotesApplicable || internalNotesPaginationEnd) {
      return undefined;
    }

    try {
      const response = await getInternalNotes({
        workspaceId,
        customerId,
        ...(internalNotePaginationKey ?? {}),
      }).unwrap();

      // Check if we've reached the end of internal notes pagination
      const notesReceived = response?.data?.messages?.length || 0;
      if (notesReceived === 0 || !response?.data?.lastKey) {
        setInternalNotesPaginationEnd(true);
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch internal notes:', error);
      return undefined;
    }
  }, [
    internalNotesApplicable,
    customerId,
    workspaceId,
    internalNotePaginationKey,
    internalNotesPaginationEnd,
    getInternalNotes,
  ]);

  const oldestNonInternalMessage = useMemo(() => {
    const msg = activeMessages.find(
      (m) => typeof m === 'object' && !m.isInternalNote
    ) as MessageEnvelope | undefined;

    return msg;
  }, [activeMessages]);

  const startTimeToken = useMemo(() => {
    if (!hasUsedGrowTokenRef.current) {
      const startToken = getQueryTypeStarttoken(signalStartTime, signalEndTime);
      if (startToken) {
        return startToken;
      }
    }
    return (oldestNonInternalMessage?.timetoken as string) || undefined;
  }, [signalStartTime, signalEndTime, oldestNonInternalMessage]);

  useEffect(() => {
    if (isSignalView && currentSignal)
      getConversationThreads({
        workspaceId,
        conversationThreadIds: [currentSignal],
      });
  }, [currentSignal]);

  // Fetch message history with intelligent note merging
  const fetchHistory = useCallback(
    async (options?: FetchHistoryOptions) => {
      if (fetchingRef.current) return null; // ref-based guard, no stale closure
      fetchingRef.current = true;
      setFetchingMessages(true);
      const isSignalConversation = !!currentSignal;
      const shouldResetExisting = !!options?.resetExisting;
      const endTimeTokenOverride = options?.endTimeToken;
      const isForwardPagination = !!endTimeTokenOverride;
      const existingMessagesForMerge = shouldResetExisting ? [] : messages;

      // Determine what needs to be fetched
      const shouldFetchMessages =
        isForwardPagination ||
        (isSignalConversation && !hasUsedGrowTokenRef.current) ||
        !paginationEnd;
      const shouldFetchNotes =
        !isForwardPagination &&
        internalNotesApplicable &&
        !internalNotesPaginationEnd;

      if (!shouldFetchMessages && !shouldFetchNotes) return null;

      setFetchingMessages(true);
      try {
        let newMessages: MessageEnvelope[] = [];
        let allMessages = [...existingMessagesForMerge];

        // Fetch regular messages if pagination not ended
        if (shouldFetchMessages) {
          const apiStartTimeToken = endTimeTokenOverride
            ? endTimeTokenOverride
            : (isSignalConversation && signalStartTime) || '0';

          const apiEndTimeToken = endTimeTokenOverride
            ? String(Date.now() * TIMETOKEN_MULTIPLIER)
            : startTimeToken || String(Date.now() * TIMETOKEN_MULTIPLIER);

          const response = await fetchMsgHistory({
            workspaceId: workspaceId || '',
            channelId,
            startTimeToken: apiStartTimeToken,
            endTimeToken: apiEndTimeToken,
            limit: 25,
          }).unwrap();
          newMessages = response.data || [];

          // if (isSignalView) {
          //   const uniqueConversationThreadIds: string[] = [
          //     ...new Set(
          //       newMessages
          //         .map((msg) => msg.message?.conversationThreadId)
          //         .filter((id): id is string => !!id)
          //     ),
          //   ];

          //   if (uniqueConversationThreadIds.length > 0)
          //     getConversationThreads({
          //       workspaceId,
          //       conversationThreadIds: uniqueConversationThreadIds,
          //     });
          // }

          if (!isForwardPagination) {
            setPaginationEnd(
              !response.pagination?.nextKey && newMessages.length < 25
            );
          }

          const filteredMessages = isZoey
            ? newMessages.filter((m) => m.message?.isZoey)
            : newMessages;

          allMessages = mergeAndSortMessages(
            existingMessagesForMerge,
            filteredMessages
          );
        }

        // Fetch internal notes if needed
        if (shouldFetchNotes) {
          const notesResponse = await fetchInternalNotes();

          if (notesResponse?.data) {
            const notes = notesResponse.data.messages || [];
            const internalNoteMessages = notes
              .filter((note) => !isUndefined(note.message))
              .map((note) => convertInternalNoteToMessage(channelId, note));

            // Merge and sort all messages with notes
            allMessages = mergeAndSortMessages(
              allMessages,
              internalNoteMessages
            );

            // Update internal notes pagination key
            if (notesResponse.data.lastKey) {
              setInternalNotePaginationKey(notesResponse.data.lastKey);
            }

            // Check if fetched notes fall within the current message range
            // If notes exist beyond current messages, we might need to fetch more
            if (notes.length > 0 && !paginationEnd) {
              const oldestMessageTimetoken = existingMessagesForMerge[0]
                ?.timetoken as number;
              const oldestNoteTimetoken = Math.min(
                ...notes.map((n) => Number(n.timetoken))
              );

              // If notes are older than our oldest message, we should continue fetching
              if (
                oldestNoteTimetoken < oldestMessageTimetoken &&
                newMessages.length === 25
              ) {
                setShouldFetchMoreNotes(true);
              }
            }
          }
        }

        if (isSignalConversation && !hasUsedGrowTokenRef.current) {
          hasUsedGrowTokenRef.current = true;
        }

        setMessages(allMessages);
        return newMessages.length;
      } catch (error) {
        console.error('Failed to fetch history:', error);
        return undefined;
      } finally {
        fetchingRef.current = false;
        setFetchingMessages(false);
      }
    },
    [
      paginationEnd,
      internalNotesPaginationEnd,
      channelId,
      messages,
      isZoey,
      workspaceId,
      fetchMsgHistory,
      internalNotesApplicable,
      fetchInternalNotes,
      setPaginationEnd,
      setMessages,
      setInternalNotePaginationKey,
      oldestNonInternalMessage,
      startTimeToken,
    ]
  );

  // Auto-fetch more notes if they exist in the timeline
  useEffect(() => {
    if (shouldFetchMoreNotes && !fetchingMessages && internalNotesApplicable) {
      setShouldFetchMoreNotes(false);
      // Trigger another fetch to get more messages/notes
      fetchHistory();
    }
  }, [
    shouldFetchMoreNotes,
    fetchingMessages,
    internalNotesApplicable,
    fetchHistory,
  ]);

  // Initialize messages on channel change
  useEffect(() => {
    if (!channelId) return;

    const hasChannelChanged = channelId !== prevChannelId;
    const hasSignalWindowChanged =
      !!currentSignal && currentSignal !== prevSignal;

    if (!hasChannelChanged && !hasSignalWindowChanged) return;

    // Reset internal notes pagination state on conversation context change
    setInternalNotesPaginationEnd(false);
    setShouldFetchMoreNotes(false);
    setPaginationEnd(false);

    if (hasSignalWindowChanged) {
      hasUsedGrowTokenRef.current = false;
      setMessages([]);
    }

    if (!initMessagesLoaded[channelId] || hasSignalWindowChanged) {
      fetchHistory({ resetExisting: hasSignalWindowChanged })
        .then(() => {
          setInitMessagesLoaded((curr) => ({ ...curr, [channelId]: true }));
        })
        .catch((e) => console.error('Failed to initialize messages:', e));
    }
  }, [
    channelId,
    currentSignal,
    prevChannelId,
    prevSignal,
    initMessagesLoaded,
    fetchHistory,
    setMessages,
    setPaginationEnd,
  ]);

  // Track unread messages
  useEffect(() => {
    if (
      !messages?.length ||
      scrolledBottom ||
      messages.length - (prevMessages?.length || 0) !== 1
    ) {
      return;
    }

    const latestMessage = messages[messages.length - 1];
    const prevLatestMessage = prevMessages?.[prevMessages.length - 1];

    if (
      latestMessage &&
      prevLatestMessage &&
      Number(latestMessage.timetoken) > Number(prevLatestMessage.timetoken)
    ) {
      setUnreadMessages((unread) => unread + 1);
    }
  }, [messages, prevMessages, scrolledBottom]);

  useEffect(() => {
    hasUsedGrowTokenRef.current = false;
  }, [channelId, currentSignal]);

  // Check if both paginations have ended.
  // Internal notes pagination only matters when notes can actually be fetched.
  const allPaginationEnded =
    paginationEnd && (!internalNotesApplicable || internalNotesPaginationEnd);

  return {
    messages,
    prevChannelId,
    scrolledBottom,
    scrolledTop,
    setScrolledTop,
    setScrolledBottom,
    unreadMessages,
    setUnreadMessages,
    groupedMessages,
    initMessagesLoaded,
    fetchHistory,
    fetchingMessages,
    hasNewMessages: (prevMessages?.length || 0) !== messages.length,
    goToBottom,
    toBottom,
    toTop,
    fetchInternalNotes,
    internalNotePaginationKey,
    internalNotesPaginationEnd,
    allPaginationEnded,
    oldestNonInternalMessageId: oldestNonInternalMessage?.message?._id,
  };
};
